import { create } from 'zustand'

const CACHED_PERMS_KEY = 'auth-perms-cache'

/** Read cached permissions from localStorage (no PII, just permission strings) */
function getCachedPermissions(): string[] {
  try {
    const raw = localStorage.getItem(CACHED_PERMS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

/** Write permissions to localStorage */
function setCachedPermissions(perms: string[]) {
  try { localStorage.setItem(CACHED_PERMS_KEY, JSON.stringify(perms)) } catch {}
}

/** Clear cached permissions (on logout) */
function clearCachedPermissions() {
  try { localStorage.removeItem(CACHED_PERMS_KEY) } catch {}
}
export interface AuthUser {
  id: string
  email: string
  first_name: string
  last_name: string
  status: string
  active_client: { id: string; name: string }
  client_ids: string[]
  clients?: { id: string; name: string }[]
  roles: { id: string; name: string; display_name?: string; is_template: boolean }[]
  permissions: string[]
  /** Queue bucket slugs the user is allowed to see, from t_iam_role_buckets. */
  buckets?: string[]
  facilities?: { id: string; name: string; client_id: string; client_name: string }[]
  is_impersonating?: boolean
  real_actor?: { id: string }
  /** ISO deadline when impersonation auto-expires (cap); drives the banner countdown. */
  impersonation_expires_at?: string
  /**
   * Server-driven feature payload from /auth/me.
   *   • announcements_module — legacy env-driven flag, kept for existing consumers.
   *   • vectors — per-client { feature_key: value } map (scope='all' user gets
   *     one Master vector; scope='own' user gets one per accessible client).
   *   • defaults — catalog defaults keyed by feature_key, used when a vector
   *     row is absent for the resolved target client.
   * Use `useFeature(key, resourceClientId?)` (shared/entitlements) — do not
   * read this shape directly.
   */
  features?: {
    announcements_module?: boolean
    vectors?: Record<string, Record<string, unknown>>
    defaults?: Record<string, unknown>
    /**
     * Per-facility effective values for the user's assigned facilities, keyed
     * by facility_id → { feature_key: value }. Only facility-scoped keys. Read
     * via useFeature(key, client, facilityId).
     */
    facilityVectors?: Record<string, Record<string, unknown>>
  }
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  isMfaRequired: boolean
  /**
   * True once the initial auth bootstrap (silent refresh + /auth/me) has settled,
   * regardless of outcome. Components that depend on auth context (e.g. data
   * polling, protected routes) must not mount until this flips true — otherwise
   * they fire requests with no Authorization header and hit a hard 401.
   */
  isBootstrapped: boolean
  /** Permissions from previous session, used to render sidebar while auth is loading */
  cachedPermissions: string[]

  setUser: (user: AuthUser) => void
  clearUser: () => void
  setLoading: (loading: boolean) => void
  setBootstrapped: (value: boolean) => void
  setMfaRequired: (required: boolean) => void

  /**
   * Check if user has a specific permission string.
   * Supports exact match: "coder_workspace:view:own"
   */
  hasPermission: (permission: string) => boolean

  /**
   * 3-dimensional permission check: module, action, scope
   * e.g., hasPermission3d('coder_workspace', 'view', 'own')
   */
  hasPermission3d: (module: string, action: string, scope?: string) => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isBootstrapped: false,
  isMfaRequired: false,
  cachedPermissions: getCachedPermissions(),

  setUser: (user) => {
    setCachedPermissions(user.permissions)
    set({ user, isAuthenticated: true, isLoading: false, isBootstrapped: true, isMfaRequired: false, cachedPermissions: user.permissions })
  },

  clearUser: () => {
    clearCachedPermissions()
    set({ user: null, isAuthenticated: false, isLoading: false, isBootstrapped: true, isMfaRequired: false, cachedPermissions: [] })
  },

  setLoading: (isLoading) => set({ isLoading }),

  setBootstrapped: (isBootstrapped) => set({ isBootstrapped }),

  setMfaRequired: (isMfaRequired) => set({ isMfaRequired }),

  hasPermission: (permission: string) => {
    const { user } = get()
    if (!user) return false
    return user.permissions.includes(permission)
  },

  hasPermission3d: (module: string, action: string, scope?: string) => {
    const { user } = get()
    if (!user) return false
    if (!scope) {
      const prefix = `${module}:${action}`
      return user.permissions.some((p) => p.startsWith(prefix))
    }
    // Use scope hierarchy: higher scope satisfies lower scope check
    const scopeLevel: Record<string, number> = { own: 1, team: 2, client: 3, all_clients: 4 }
    const requiredLevel = scopeLevel[scope] ?? 0
    const prefix = `${module}:${action}:`
    return user.permissions.some((p) => {
      if (!p.startsWith(prefix)) return false
      const permScope = p.slice(prefix.length)
      return (scopeLevel[permScope] ?? 0) >= requiredLevel
    })
  },
}))
